package fr.frux.feature.profile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.viewmodel.compose.viewModel
import fr.frux.core.ads.AdsController
import fr.frux.core.ads.RewardedResult
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.io.FileExporter
import fr.frux.core.common.push.PushRegistrar
import fr.frux.shared.domain.favorites.FavoritesStore
import fr.frux.shared.domain.model.User
import fr.frux.shared.domain.model.UserProfile
import fr.frux.shared.domain.notification.NotificationBadgeStore
import fr.frux.shared.domain.repository.AuthRepository
import fr.frux.shared.domain.repository.DeleteAccountOutcome
import fr.frux.shared.domain.repository.SupportRepository
import fr.frux.shared.domain.repository.UserRepository
import fr.frux.shared.domain.session.SessionState
import fr.frux.shared.domain.session.SessionStore
import fr.frux.shared.identity.IdentityPolicy
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.time.Clock
import kotlin.time.Duration.Companion.seconds
import kotlin.time.ExperimentalTime

/**
 * @param user `null` pendant une transition de session (juste après déconnexion, avant que
 *   l'appelant n'ait retiré l'écran du back stack) : l'écran n'affiche alors rien plutôt que de
 *   crasher sur un email absent.
 * @param isExporting Appel d'export RGPD en vol (#194).
 * @param transientMessage Message ponctuel (succès/échec d'export, échec de renvoi de code) —
 *   vit dans l'état du VM et non dans un effet d'écran, l'entryProvider Nav3 étant recréé à
 *   chaque changement de pile (#145).
 * @param deletion Étape courante du flux de suppression de compte (#194, #172).
 * @param unreadNotifications Compteur de notifications non lues (#225 P0), reflet de
 *   [NotificationBadgeStore.unreadCount] — affiché sur l'entrée « Activité » de cet écran.
 * @param logoutAllConfirmationVisible Dialogue de confirmation avant « Déconnecter tous les
 *   appareils » (#26, C8) : geste plus radical qu'un simple logout, il coupe aussi les autres
 *   appareils de l'utilisateur.
 * @param contributions Compteurs publics du compte connecté (#156, #227) — `null` tant que non
 *   chargés ou en échec, jamais de zéro inventé : les tuiles s'absentent plutôt que d'afficher
 *   un chiffre faux.
 * @param favoritesCount Nombre de favoris du compte connecté (#442), lu en direct dans
 *   [FavoritesStore] pour suivre un ajout ou un retrait fait ailleurs. Même règle que
 *   [contributions] : `null` tant qu'aucun rafraîchissement n'a abouti, le store valant un
 *   ensemble vide avant le premier chargement.
 * @param pendingDonateUrl URL de don à ouvrir, consommée par l'écran (#319) : one-shot dans
 *   l'état du VM et non un effet d'écran, l'entryProvider Nav3 étant recréé à chaque changement
 *   de pile (#145). Vaut l'URL signée du serveur si l'appel a réussi, sinon [SUPPORT_URL] nue.
 * @param supportVisible Ligne « Soutenir Frux » affichée (#473) : jamais sur Android ni iOS, où
 *   les magasins réservent l'exception « dons » aux organismes caritatifs.
 */
data class ProfileState(
    val user: User? = null,
    val isExporting: Boolean = false,
    val transientMessage: ProfileMessage? = null,
    val deletion: AccountDeletionState = AccountDeletionState.Idle,
    val unreadNotifications: Int = 0,
    val logoutAllConfirmationVisible: Boolean = false,
    val contributions: UserProfile? = null,
    val favoritesCount: Int? = null,
    val pendingDonateUrl: String? = null,
    val supportVisible: Boolean = false,
    /** Ligne « Regarder une vidéo pour 24 h sans pub » (#531) : publicité active pour ce compte. */
    val rewardedOfferVisible: Boolean = false,
    /** Vidéo en cours de chargement ou de lecture : la ligne reste là, désactivée (#531). */
    val rewardedLoading: Boolean = false,
    /** Vidéo vue, droit pas encore visible : la ligne reste là, désactivée « en cours de validation » (#531). */
    val rewardedValidating: Boolean = false,
    /** Résultat de la dernière vidéo, à montrer une fois en snackbar puis consommer. */
    val rewardedResult: RewardedResult? = null,
    /** Carte d'accompagnement à montrer (rappel du premier arbre ou enrichissement), `null` sinon. */
    val nudge: ContributionNudge? = null,
)

/**
 * URL de la page de soutien (cagnotte de dons, #240), repli quand le serveur ne peut pas rendre
 * l'URL signée (réseau, session expirée) : l'entrée reste réactive même hors ligne (#319).
 */
const val SUPPORT_URL = "https://frux-app.fr/soutenir"

/**
 * Message ponctuel du profil (#176), typé : le libellé est résolu par l'écran. Deux confirmations
 * (déconnexion globale, export prêt), un cas de plateforme (export non proposable) et tout échec
 * d'appel.
 */
sealed interface ProfileMessage {
    data object AllDevicesLoggedOut : ProfileMessage

    data object ExportReady : ProfileMessage

    /**
     * Export réussi côté API mais non proposable côté plateforme (iOS/desktop, cf. #194) : reste
     * un échec du point de vue de l'utilisateur, il ne récupère rien.
     */
    data object ExportNotSupported : ProfileMessage

    data class Failure(
        val error: AppError,
    ) : ProfileMessage
}

/** Pourquoi la confirmation de suppression a été refusée (#194, typée en #176). */
sealed interface AccountDeletionError {
    /** Garde locale : le code n'a pas la longueur d'[IdentityPolicy.OTP_CODE_LENGTH] chiffres. */
    data object CodeInvalidFormat : AccountDeletionError

    /** `DeleteAccountOutcome.InvalidCode` : code faux ou expiré côté serveur. */
    data object CodeInvalidOrExpired : AccountDeletionError

    data class Failure(
        val error: AppError,
    ) : AccountDeletionError
}

/**
 * Machine à états du flux de suppression (#194) : [Warning] (avertissement irréversible) →
 * [RequestingCode] (réutilise la demande de code de la connexion, ADR 0012) → [EnterCode]
 * (saisie et vérification). Le succès ne produit pas d'état terminal dédié : il déclenche
 * [SessionStore.logout], et `App` réagit à la sortie de session comme pour toute déconnexion (#48).
 */
sealed interface AccountDeletionState {
    data object Idle : AccountDeletionState

    data object Warning : AccountDeletionState

    data object RequestingCode : AccountDeletionState

    data class EnterCode(
        val code: String = "",
        val isSubmitting: Boolean = false,
        /** Refus de la confirmation (#176), typé : le libellé est résolu par l'écran. */
        val error: AccountDeletionError? = null,
    ) : AccountDeletionState
}

/**
 * Profil de l'utilisateur connecté.
 *
 * La déconnexion (directe ou consécutive à une suppression de compte) tourne dans
 * [viewModelScope] et non dans le scope du composable (#88) : quitter l'écran ne doit pas
 * l'annuler en cours de route. Le ViewModel ne navigue pas — il change l'état de session, et
 * `App` réagit à la transition sortante (#48).
 */
class ProfileViewModel(
    private val sessionStore: SessionStore,
    private val authRepository: AuthRepository,
    private val fileExporter: FileExporter,
    private val notificationBadgeStore: NotificationBadgeStore,
    private val pushRegistrar: PushRegistrar,
    private val userRepository: UserRepository,
    private val supportRepository: SupportRepository,
    private val favoritesStore: FavoritesStore,
    private val options: ProfileOptions,
    /** Vidéo récompensée (#531), donné par `FruxCompositionRoot`. `null` dans les tests qui ne l'exercent pas. */
    private val adsController: AdsController? = null,
) : ViewModel() {
    /** Tout ce que la session ne porte pas elle-même : export en vol, message ponctuel, suppression. */
    private val extraState = MutableStateFlow(ProfileExtraState())

    val state: StateFlow<ProfileState> =
        combine(
            sessionStore.state,
            extraState,
            notificationBadgeStore.unreadCount,
            favoritesStore.favoriteIds,
            combine(
                adsController?.canOfferRewarded ?: flowOf(false),
                adsController?.rewardedPending ?: flowOf(false),
            ) { offer, pending -> offer to pending },
        ) { session, extra, unread, favoriteIds, (rewardedOffer, rewardedPending) ->
            ProfileState(
                user = (session as? SessionState.Authenticated)?.user,
                isExporting = extra.isExporting,
                transientMessage = extra.transientMessage,
                deletion = extra.deletion,
                unreadNotifications = unread,
                logoutAllConfirmationVisible = extra.logoutAllConfirmationVisible,
                contributions = extra.contributions,
                favoritesCount = if (extra.favoritesLoaded) favoriteIds.size else null,
                pendingDonateUrl = extra.pendingDonateUrl,
                supportVisible = options.donationsAllowed,
                nudge = extra.nudge,
                rewardedOfferVisible = rewardedOffer,
                rewardedLoading = extra.rewardedInFlight,
                rewardedValidating = rewardedPending,
                rewardedResult = extra.rewardedResult,
            )
        }.stateIn(
            scope = viewModelScope,
            // Eagerly plutôt que WhileSubscribed : l'état initial est déjà connu du store, un
            // démarrage paresseux ferait clignoter un profil vide à l'ouverture de l'écran.
            started = SharingStarted.Eagerly,
            initialValue =
                ProfileState(
                    user = (sessionStore.state.value as? SessionState.Authenticated)?.user,
                    supportVisible = options.donationsAllowed,
                ),
        )

    private val nudgeController =
        options.nudgeStore?.let { store ->
            ContributionNudgeController(store, viewModelScope, options.now) { nudge ->
                extraState.update { it.copy(nudge = nudge) }
            }
        }

    init {
        viewModelScope.launch {
            sessionStore.state
                .map { (it as? SessionState.Authenticated)?.user?.id }
                .distinctUntilChanged()
                .collect { userId ->
                    if (userId == null) {
                        extraState.update { it.copy(contributions = null, favoritesLoaded = false) }
                        nudgeController?.onContributions(null)
                    } else {
                        // Compteurs publics (#227) relus a chaque changement de session (connexion,
                        // changement de compte). Le ViewModel survit lui a une navigation vers « Mes
                        // arbres » (rememberViewModelStoreNavEntryDecorator, App.kt) : ce collect ne
                        // rejoue pas au retour sur l'ecran, d'ou refreshContributions() appele par
                        // ProfileScreen a chaque reprise. En parallele et non en sequence : deux
                        // appels reseau independants (#442).
                        coroutineScope {
                            launch { loadContributions(userId) }
                            launch { refreshFavorites() }
                        }
                    }
                }
        }
    }

    /** Relit les compteurs du compte connecte. Échec silencieux : pas de zeros inventes (#156). */
    private suspend fun loadContributions(userId: Long) {
        userRepository.getUserProfile(userId).onSuccess { profile ->
            extraState.update { it.copy(contributions = profile) }
            nudgeController?.onContributions(profile)
        }
    }

    /**
     * Rafraîchit le store des favoris (#442). Un échec ne rabat pas un compteur déjà connu : le
     * store garde son dernier état, qui reste juste hors ligne.
     */
    private suspend fun refreshFavorites() {
        favoritesStore.refresh().onSuccess { extraState.update { it.copy(favoritesLoaded = true) } }
    }

    /**
     * Reprise de l'ecran (#156, revue de fin de lot) : le ViewModel est permanent tant que
     * `Route.Profile` reste dans la pile, un retour depuis « Mes arbres » ne rejoue donc pas
     * l'init. Appele par [fr.frux.feature.profile.ProfileScreen] a chaque entree en composition.
     * Rafraîchit aussi les favoris (#442), même raison que les compteurs de contribution.
     */
    fun refreshContributions() {
        val userId = (sessionStore.state.value as? SessionState.Authenticated)?.user?.id ?: return
        viewModelScope.launch {
            coroutineScope {
                launch { loadContributions(userId) }
                launch { refreshFavorites() }
            }
        }
    }

    fun logout() {
        viewModelScope.launch {
            // Avant l'invalidation de session : la révocation est un appel API authentifié (#252).
            // Un échec ne bloque pas la déconnexion — l'intention de partir gagne toujours, et le
            // jeton sera de toute façon réattribué au prochain compte qui se connecte sur cet
            // appareil (contrainte `UNIQUE (token)`, V24). Plafonnée (revue T15 IMPORTANT 4) : la
            // déconnexion ne doit pas rester suspendue derrière un aller-retour réseau qui traîne.
            withTimeoutOrNull(3.seconds) { runCatching { pushRegistrar.unregister() } }
            nudgeController?.forget()
            sessionStore.logout()
        }
    }

    /** Ferme la carte d'accompagnement : elle se tait sept jours. */
    fun dismissNudge() {
        nudgeController?.dismiss()
    }

    /** Ouvre le dialogue de confirmation avant « Déconnecter tous les appareils » (#26, C8). */
    fun requestLogoutAllConfirmation() {
        extraState.update { it.copy(logoutAllConfirmationVisible = true) }
    }

    /** Abandon du dialogue de confirmation, sans effet sur la session. */
    fun cancelLogoutAllConfirmation() {
        extraState.update { it.copy(logoutAllConfirmationVisible = false) }
    }

    /**
     * « Déconnecter tous les appareils » (#26, ADR 0019) : contrairement à [logout], un échec
     * laisse la session locale intacte (message d'erreur), le geste n'est confirmé que si le
     * serveur a effectivement révoqué toutes les familles de sessions. Un succès l'annonce
     * brièvement (#26, C8) : sans retour, rien à l'écran ne distingue ce geste radical d'un clic
     * sans effet.
     */
    fun confirmLogoutAllDevices() {
        extraState.update { it.copy(logoutAllConfirmationVisible = false) }
        viewModelScope.launch {
            sessionStore.logoutAll().fold(
                onSuccess = {
                    extraState.update { it.copy(transientMessage = ProfileMessage.AllDevicesLoggedOut) }
                },
                onFailure = { error ->
                    extraState.update { it.copy(transientMessage = ProfileMessage.Failure(error.asAppError())) }
                },
            )
        }
    }

    /** Message ponctuel affiché — l'écran l'acquitte une fois montré (SnackBar). */
    fun onMessageShown() {
        extraState.update { it.copy(transientMessage = null) }
    }

    /**
     * « Regarder une vidéo pour 24 h sans pub » (#531). Lancée dans le scope du ViewModel, qui survit à la
     * recréation des entrées Nav3, jamais dans celui de l'écran. Un second appui pendant la vidéo
     * est ignoré.
     */
    fun watchRewardedAd() {
        val ads = adsController ?: return
        if (extraState.value.rewardedInFlight) return
        extraState.update { it.copy(rewardedInFlight = true) }
        viewModelScope.launch {
            try {
                val result = ads.watchRewardedForAdFree()
                extraState.update { it.copy(rewardedResult = result.takeUnless { r -> r == RewardedResult.DISMISSED }) }
            } finally {
                extraState.update { it.copy(rewardedInFlight = false) }
            }
        }
    }

    fun onRewardedMessageShown() {
        extraState.update { it.copy(rewardedResult = null) }
    }

    /**
     * Demande l'URL de don signée au serveur (#319) : un don initié depuis le profil doit se
     * rattacher au compte, comme celui du prompt. Aucune règle métier ici : en cas d'échec
     * (réseau, session expirée), repli sur l'URL nue [SUPPORT_URL] — l'entrée reste réactive
     * même hors ligne.
     *
     * Garde de requête en vol (revue), même patron que [exportAccount] avec `isExporting` : sans
     * elle, un double clic pendant la latence réseau ouvre deux URLs — la seconde réponse écrase
     * [ProfileExtraState.pendingDonateUrl] après que la première ait déjà été acquittée par
     * l'écran. Ignoré si un appel est déjà en vol ou si une URL attend encore d'être ouverte.
     */
    fun onSupportClicked() {
        // Ligne absente sur Android et iOS (#473), la garde couvre un appel qui passerait outre.
        if (!options.donationsAllowed) return
        if (extraState.value.isSupportRequestInFlight || extraState.value.pendingDonateUrl != null) return
        extraState.update { it.copy(isSupportRequestInFlight = true) }
        viewModelScope.launch {
            val url = supportRepository.donateUrl().getOrElse { SUPPORT_URL }
            extraState.update { it.copy(isSupportRequestInFlight = false, pendingDonateUrl = url) }
        }
    }

    /** URL de don ouverte par l'écran — acquittée pour ne pas la rouvrir à la recomposition suivante. */
    fun onDonateUrlOpened() {
        extraState.update { it.copy(pendingDonateUrl = null) }
    }

    /**
     * Exporte le compte courant et propose le fichier à l'utilisateur via [FileExporter]. Un
     * export réussi côté API mais non proposable côté plateforme (iOS/desktop, cf. #194) reste
     * un échec du point de vue de l'utilisateur : il ne récupère rien.
     */
    @OptIn(ExperimentalTime::class)
    fun exportAccount() {
        viewModelScope.launch {
            extraState.update { it.copy(isExporting = true) }
            authRepository.exportAccount().fold(
                onSuccess = { json ->
                    val fileName = "frux-export-${Clock.System.now().epochSeconds}.json"
                    val proposed = fileExporter.export(fileName, json)
                    extraState.update {
                        it.copy(
                            isExporting = false,
                            transientMessage =
                                if (proposed) ProfileMessage.ExportReady else ProfileMessage.ExportNotSupported,
                        )
                    }
                },
                onFailure = { error ->
                    extraState.update {
                        it.copy(isExporting = false, transientMessage = ProfileMessage.Failure(error.asAppError()))
                    }
                },
            )
        }
    }

    /** Ouvre le premier écran du flux : l'avertissement, avant toute demande de code. */
    fun startAccountDeletion() {
        extraState.update { it.copy(deletion = AccountDeletionState.Warning) }
    }

    /** Abandon à n'importe quelle étape — avertissement ou saisie du code. */
    fun cancelAccountDeletion() {
        extraState.update { it.copy(deletion = AccountDeletionState.Idle) }
    }

    /**
     * Avertissement acquitté : demande un code frais (même mécanisme que la connexion, ADR 0012)
     * puis ouvre la saisie. Sert aussi au renvoi de code depuis l'étape [AccountDeletionState.EnterCode].
     */
    fun requestDeletionCode() {
        val email = state.value.user?.email ?: return
        viewModelScope.launch {
            extraState.update { it.copy(deletion = AccountDeletionState.RequestingCode) }
            // purpose=DELETE_ACCOUNT (#274) : ce code ne doit confirmer rien d'autre que
            // l'effacement de compte, pas une simple connexion.
            sessionStore.requestCode(email, purpose = "DELETE_ACCOUNT").fold(
                onSuccess = {
                    extraState.update { it.copy(deletion = AccountDeletionState.EnterCode()) }
                },
                onFailure = { error ->
                    extraState.update {
                        it.copy(
                            deletion = AccountDeletionState.Idle,
                            transientMessage = ProfileMessage.Failure(error.asAppError()),
                        )
                    }
                },
            )
        }
    }

    fun onDeletionCodeChange(code: String) {
        extraState.update { extra ->
            val current = extra.deletion as? AccountDeletionState.EnterCode ?: return@update extra
            extra.copy(deletion = current.copy(code = code, error = null))
        }
    }

    /**
     * Vérifie le code et efface le compte. Sur succès, déconnecte localement : l'API a déjà
     * invalidé le jeton côté serveur (#172), cet appel purge les credentials côté client et
     * fait passer la session à `Anonymous` — `App` s'occupe du retour à l'accueil (#48).
     */
    fun confirmAccountDeletion() {
        val current = extraState.value.deletion as? AccountDeletionState.EnterCode ?: return
        if (current.code.length != IdentityPolicy.OTP_CODE_LENGTH || !current.code.all(Char::isDigit)) {
            extraState.update {
                it.copy(
                    deletion =
                        current.copy(error = AccountDeletionError.CodeInvalidFormat),
                )
            }
            return
        }

        viewModelScope.launch {
            extraState.update { it.copy(deletion = current.copy(isSubmitting = true, error = null)) }
            authRepository.deleteAccount(current.code).fold(
                onSuccess = { outcome ->
                    when (outcome) {
                        DeleteAccountOutcome.Deleted -> {
                            extraState.update { it.copy(deletion = AccountDeletionState.Idle) }
                            // Pas de pushRegistrar.unregister() ici : l'effacement RGPD a déjà
                            // supprimé les jetons côté serveur (Task 9), un appel de révocation
                            // ne trouverait plus rien à révoquer.
                            nudgeController?.forget()
                            sessionStore.logout()
                        }

                        DeleteAccountOutcome.InvalidCode -> {
                            extraState.update {
                                it.copy(
                                    deletion =
                                        current.copy(
                                            isSubmitting = false,
                                            error = AccountDeletionError.CodeInvalidOrExpired,
                                        ),
                                )
                            }
                        }
                    }
                },
                onFailure = { error ->
                    extraState.update {
                        it.copy(
                            deletion =
                                current.copy(
                                    isSubmitting = false,
                                    error = AccountDeletionError.Failure(error.asAppError()),
                                ),
                        )
                    }
                },
            )
        }
    }
}

private data class ProfileExtraState(
    val isExporting: Boolean = false,
    val transientMessage: ProfileMessage? = null,
    val deletion: AccountDeletionState = AccountDeletionState.Idle,
    val logoutAllConfirmationVisible: Boolean = false,
    val contributions: UserProfile? = null,
    val favoritesLoaded: Boolean = false,
    val pendingDonateUrl: String? = null,
    val isSupportRequestInFlight: Boolean = false,
    val rewardedResult: RewardedResult? = null,
    val rewardedInFlight: Boolean = false,
    val nudge: ContributionNudge? = null,
)

/** Contrat de fabrique du [ProfileViewModel], honoré par le composition root (#43). */
fun interface ProfileViewModelFactory {
    fun create(): ProfileViewModel
}

/** Fabrique installée par le composition root ; sans défaut, pour que l'oubli de câblage crie. */
val LocalProfileViewModelFactory =
    staticCompositionLocalOf<ProfileViewModelFactory> {
        error("ProfileViewModelFactory absente : le composition root doit l'installer (cf. FruxCompositionRoot, #43)")
    }

/** Fabrique le ProfileViewModel depuis la fabrique fournie par le root. */
@Composable
fun rememberProfileViewModel(): ProfileViewModel {
    val factory = LocalProfileViewModelFactory.current
    return viewModel { factory.create() }
}
